import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { Hyperlink } from '../../../components/atoms/Hyperlink';
import { getTitle } from '../../../lib/NavigationItemsUtils';
import { Namespace } from '../../../translation/namespace';
import { StateIndicator } from '../../../_lib/components/StateIndicator';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { ExtendedInfoSVG } from '../../../_lib/components/ExtendedInfo';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { PagePackStates } from '../../../types/api/PagePackState';
import { BreadcrumbNavItem } from '../../../types/api/NavItem';

function ReadRestrictionIcon({ linkName }: { linkName: string }) {
  return (
    <>
      {linkName}
      <span className="breadcrumb__flyout-link-restricted">
        <SVG inverted svgSpriteId={Icons.CLOSED_LOCK} />
      </span>
    </>
  );
}

function FocusableReadRestrictionIcon() {
  const { t } = useTranslation(Namespace.Breadcrumb);
  return (
    <span className="breadcrumb__flyout-link-restricted">
      <ExtendedInfoSVG
        helpText={t('readRestrictionText')}
        tooltipPlacement={TooltipPlacement.RIGHT}
        svgSpriteID={Icons.CLOSED_LOCK}
        modifier="margin-left-auto"
      />
    </span>
  );
}

type GroupLabel = 'applications' | 'sections' | 'contents' | 'processes' | 'organizations' | 'locations' | 'topics';
export type BreadcrumbFlyoutProps = {
  groups: {
    groupLabel: GroupLabel;
    navItems: BreadcrumbNavItem[];
  }[];
};

const getStateIndicator = (packState: PagePackStates) => {
  if (packState === 'onlyDraftVersion') {
    return (
      <span className="breadcrumb__flyout-state-indicator">
        <StateIndicator status="red" modifier="flyout-navigation-item" />
      </span>
    );
  }

  if (packState === 'template') {
    return (
      <span className="breadcrumb__flyout-state-indicator">
        <StateIndicator status="purple" modifier="flyout-navigation-item" />
      </span>
    );
  }

  return null;
};

/**
 * A flyout that is used by BreadcrumbItem it shows all navItems as Hyperlinks,
 * except the navItem to the current page. The navItem of the current page is
 * highlighted and has no Hyperlink.
 * NavItems with a read restriction are shown with a "closed-lock" icon.
 * BreadcrumbFlyout renders the navItems in its given groups.
 */
export function BreadcrumbFlyout({ groups = [] }: BreadcrumbFlyoutProps) {
  const { t } = useSonTranslation('Breadcrumb');

  const renderCurrentNavigationItem = (navItem: BreadcrumbNavItem) => {
    return (
      // disabled "link" here because link is redundant to previous breadcrumb link,
      // and it's NOT always the current location on the page, so no aria-current here!
      // eslint-disable-next-line jsx-a11y/anchor-is-valid
      <a className="breadcrumb__flyout-link font-regular-13-18" role="link" aria-disabled="true" key={navItem.link.url}>
        {getStateIndicator(navItem.packState)}
        <span className="breadcrumb__flyout-linkname">
          {navItem.link.name}
          {navItem.hasReadRestrictions && <FocusableReadRestrictionIcon />}
        </span>
      </a>
    );
  };

  const renderNavigationItem = (navItem: BreadcrumbNavItem) => {
    return (
      <Hyperlink
        key={navItem.link.url}
        to={navItem.link.url}
        className="breadcrumb__flyout-link font-regular-13-18"
        title={getTitle(navItem)}
        tooltipPlacement={TooltipPlacement.RIGHT}
      >
        {getStateIndicator(navItem.packState)}
        <span className="breadcrumb__flyout-linkname">
          {navItem.hasReadRestrictions ? <ReadRestrictionIcon linkName={navItem.link.name} /> : navItem.link.name}
        </span>
      </Hyperlink>
    );
  };

  return (
    <div className="breadcrumb__flyout">
      {groups.map(({ groupLabel, navItems }) => (
        <div className="breadcrumb__siblings-group" key={groupLabel}>
          {groupLabel && (
            <span
              className="breadcrumb__siblings-group-headline font-light-12-16"
              // visible group headline only
              // prevent duplicate headline in screen readers
              aria-hidden="true"
            >
              <span className="breadcrumb__siblings-group-headline-text">{t(`groupLabel.${groupLabel}`)}</span>
            </span>
          )}
          {navItems && navItems.length > 0 && (
            <ul aria-label={groupLabel ? t(`groupLabel.${groupLabel}`) : undefined}>
              {navItems.map(navItem => (
                <li key={navItem.link.url} className="breadcrumb__flyout-list-item">
                  {navItem.position.includes('self')
                    ? renderCurrentNavigationItem(navItem)
                    : renderNavigationItem(navItem)}
                </li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
}
